"use client";
import { UserInfo } from "@cmp-src/components/UserInfo/UserInfo";
import { UserPropertyType } from "@gettactic/api";
import { useTeamAttendanceFilters } from "@/features/teams/components/TeamSchedule/hooks/useTeamAttendanceFilters";

import { useAuthenticated } from "@gettactic/hooks";
import { SelectTeams } from "@/features/teams/components/SelectTeams";
import { useEffect, useRef } from "react";
import { InfiniteScroll } from "@gettactic/components/src/components/InfiniteScroll/InfiniteScroll";
import { MeetingUserGrid } from "@cmp-src/components/meeting/MeetingUserGrid/MeetingUserGrid";
import { useSettings } from "@gettactic/hooks";

const UsersSkeleton = ({ count = 1 }: { count?: number }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex items-center rounded p-2">
          <div className="h-8 w-8 animate-pulse rounded-full bg-neutral-200" />
          <div className="ml-2 flex-1">
            <div className="mb-2 h-3 w-3/4 animate-pulse rounded bg-neutral-200" />
            <div className="h-2 w-1/2 animate-pulse rounded bg-neutral-200" />
          </div>
        </div>
      ))}
    </>
  );
};

export const UsersInOffice = ({
  officeId,
  onUserCountChange,
  isRoom
}: {
  officeId: string;
  onUserCountChange?: (count: number) => void;
  isRoom?: boolean;
}) => {
  const { isOfficePrivacyEnabled } = useSettings();
  const isOrgPrivacyEnabled = isOfficePrivacyEnabled(officeId);
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const {
    filters: dashboardFilters,
    isComponentLoading,
    totalUsers,
    tableOverviewData,
    setTeams,
    setOfficesId,
    handleSaveFilterData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice: { id: officeId }
  });

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    handleSaveFilterData(UserPropertyType.LastOfficesFilter, [officeId]);
    setOfficesId([officeId]);
  }, [officeId]);

  useEffect(() => {
    onUserCountChange?.(totalUsers);
  }, [totalUsers, onUserCountChange]);

  return (
    <div ref={scrollRef}>
      {isRoom && (
        <div className="flex items-center justify-between pt-4 md:pt-0">
          <div className="text-sm font-semibold">
            In the office ({totalUsers})
          </div>
          {!isOrgPrivacyEnabled && (
            <div className="flex justify-end">
              <SelectTeams
                includeFavs={true}
                teams={dashboardFilters.teams}
                onChange={(teamIds: string[] | string) => {
                  const teamIdsArray = Array.isArray(teamIds)
                    ? teamIds
                    : [teamIds];
                  setTeams(teamIdsArray);
                }}
                variant="ghost"
                splitView={true}
                contentWidth="500px"
              />
            </div>
          )}
        </div>
      )}
      <MeetingUserGrid classNames="pt-2 pb-4 md:pt-6 md:pb-0 flex flex-col">
        {tableOverviewData.map((user) => (
          <UserInfo
            name={user.user.name}
            profile_photo_url={user.user?.profile_photo_url ?? ""}
            title={user.user.title}
            key={user.user.name}
          />
        ))}

        {/* Testing skeleton outside InfiniteScroll */}
        {/* <UsersSkeleton /> */}

        {hasNextPage && (
          <InfiniteScroll
            hasNextPage={!!hasNextPage}
            fetchNextPage={fetchNextPage || (() => {})}
            isFetchingNextPage={!!isFetchingNextPage}
            disabled={isComponentLoading || tableOverviewData.length === 0}
            offset={200}
            scrollRef={scrollRef}
            spinner={<UsersSkeleton />}
          />
        )}
      </MeetingUserGrid>
    </div>
  );
};
